import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { cn } from '@/utils/cn';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import Button from '@/components/ui/Button';
import OptimizedImage from '@/components/ui/OptimizedImage';
import { categoryItems, menuItems } from '@/data/foods';
import { specialtiesSection, menuPage, apiErrors } from '@/data/content';
import api from '@/Services/api';
import { API } from '@/Services/apiConfig';
import { reportError } from '@/Services/errorReporter';
import { useToast } from '@/Context/ToastContext';
import { formatPrice } from '@/Services/format';
import { FoodUpdate } from '@/types/menu';
import { useFoodCatalog } from '@/hooks/useFoodCatalog';

const inputClass =
    'w-full px-4 py-3 rounded-xl border border-app-muted/30 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-app-text dark:text-white placeholder-app-muted focus:bg-white dark:focus:bg-gray-600 focus:ring-2 focus:ring-app-primary/20 focus:border-app-primary outline-none transition-all duration-200 text-sm';
const labelClass = 'block text-sm font-semibold text-app-text dark:text-gray-300 mb-1.5';

const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
};

export default function CreateMenuPage() {
    const [searchParams] = useSearchParams();
    const isFixed = searchParams.get('fixed') !== 'false';

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        discount: '0',
    });
    const [selectedFoodIds, setSelectedFoodIds] = useState<Set<number>>(new Set());
    const [pickCounts, setPickCounts] = useState<Record<number, number>>({});
    const { data: foodUpdates = new Map<number, FoodUpdate>() } = useFoodCatalog();
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();

    const getFoodWeight = useCallback(
        (food: (typeof menuItems)[number]) => Number(foodUpdates.get(food.id)?.weight_percentage ?? food.weight_percentage ?? 0),
        [foodUpdates]
    );

    const getFoodUnit = useCallback(
        (food: (typeof menuItems)[number]) => foodUpdates.get(food.id)?.food_unit ?? food.food_unit ?? 'g',
        [foodUpdates]
    );

    const getFoodPriceRange = useCallback(
        (food: (typeof menuItems)[number]) => {
            const update = foodUpdates.get(food.id);
            if (update) {
                const variants = update.variants ?? [];
                let foodVariants = variants.filter((v) => v.type === 'menu');
                if (foodVariants.length === 0) {
                    foodVariants = variants.filter((v) => v.type === 'food');
                }
                if (foodVariants.length > 0) {
                    const prices = foodVariants
                        .map((v) => Number(v.price))
                        .filter((price) => Number.isFinite(price));

                    if (prices.length > 0) {
                        return {
                            minPrice: Math.min(...prices),
                            maxPrice: Math.max(...prices),
                        };
                    }
                }
                if (update.price != null && Number.isFinite(Number(update.price))) {
                    return { minPrice: Number(update.price), maxPrice: Number(update.price) };
                }
            }
            const fallback = Number(food.price ?? 0);
            return { minPrice: fallback, maxPrice: fallback };
        },
        [foodUpdates]
    );

    // Auto-set pickCount from 0 to 1 on first selection per category
    useEffect(() => {
        if (isFixed) return;
        setPickCounts((prev) => {
            const next = { ...prev };
            categoryItems.forEach((cat) => {
                const selected = menuItems.filter(
                    (f) => f.category_id === cat.id && selectedFoodIds.has(f.id)
                ).length;
                if (selected > 0 && (prev[cat.id] ?? 0) === 0) {
                    next[cat.id] = 1;
                }
            });
            return next;
        });
    }, [selectedFoodIds, isFixed]);

    const computeMinWeight = useCallback((counts: Record<number, number>): number => {
        let total = 0;
        for (const catIdStr of Object.keys(counts)) {
            const catId = Number(catIdStr);
            const count = counts[catId];
            if (count <= 0) continue;
            const catFoods = menuItems
                .filter((f) => f.category_id === catId)
                .sort((a, b) => getFoodWeight(a) - getFoodWeight(b))
                .slice(0, count);
            total += catFoods.reduce((sum, f) => sum + getFoodWeight(f), 0);
        }
        return total;
    }, [getFoodWeight]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const toggleFood = (foodId: number) => {
        if (isFixed) {
            setSelectedFoodIds((prev) => {
                const next = new Set(prev);
                if (next.has(foodId)) next.delete(foodId);
                else next.add(foodId);
                return next;
            });
        } else {
            setSelectedFoodIds((prev) => {
                const next = new Set(prev);
                if (next.has(foodId)) {
                    next.delete(foodId);
                } else if (prev.size < 10) {
                    next.add(foodId);
                }
                return next;
            });
        }
    };

    const handlePickCountChange = (catId: number, delta: number) => {
        if (isFixed) return;

        const current = pickCounts[catId] ?? 0;
        const nextVal = current + delta;
        if (nextVal < 0) return;

        const catFoods = menuItems.filter((f) => f.category_id === catId);
        const selected = catFoods.filter((f) => selectedFoodIds.has(f.id));

        if (nextVal < selected.length) {
            const excess = selected.length - nextVal;
            const toRemove = selected.slice(-excess).map((f) => f.id);
            setSelectedFoodIds((prev) => {
                const next = new Set(prev);
                toRemove.forEach((id) => next.delete(id));
                return next;
            });
        }

        const otherTotal = Object.entries(pickCounts)
            .filter(([id]) => Number(id) !== catId)
            .reduce((sum, [, c]) => sum + c, 0);
        if (nextVal + otherTotal > 10) return;

        const testCounts = { ...pickCounts, [catId]: nextVal };
        if (computeMinWeight(testCounts) > 100) return;

        setPickCounts((prev) => ({ ...prev, [catId]: nextVal }));
    };

    const totalIndividualPrice = useMemo(() => {
        if (isFixed) {
            let total = 0;
            menuItems
                .filter((f) => selectedFoodIds.has(f.id))
                .forEach((food) => {
                    total += getFoodPriceRange(food).minPrice;
                });
            return { min: total, max: total };
        }
        let minTotal = 0;
        let maxTotal = 0;
        categoryItems.forEach((cat) => {
            const count = pickCounts[cat.id] ?? 0;
            if (count <= 0) return;
            const selected = menuItems.filter((f) => f.category_id === cat.id && selectedFoodIds.has(f.id));
            const priceRanges = selected.map(getFoodPriceRange);
            const byMin = [...priceRanges].sort((a, b) => a.minPrice - b.minPrice).slice(0, count);
            minTotal += byMin.reduce((sum, p) => sum + p.minPrice, 0);
            const byMax = [...priceRanges].sort((a, b) => b.maxPrice - a.maxPrice).slice(0, count);
            maxTotal += byMax.reduce((sum, p) => sum + p.maxPrice, 0);
        });
        return { min: minTotal, max: maxTotal };
    }, [selectedFoodIds, getFoodPriceRange, isFixed, pickCounts]);

    const totalWeight = useMemo(() => {
        if (isFixed) {
            return menuItems
                .filter((f) => selectedFoodIds.has(f.id))
                .reduce((sum, food) => sum + getFoodWeight(food), 0);
        }
        let total = 0;
        categoryItems.forEach((cat) => {
            const count = pickCounts[cat.id] ?? 0;
            if (count <= 0) return;
            const selected = menuItems
                .filter((f) => f.category_id === cat.id && selectedFoodIds.has(f.id))
                .sort((a, b) => getFoodWeight(a) - getFoodWeight(b))
                .slice(0, count);
            total += selected.reduce((sum, f) => sum + getFoodWeight(f), 0);
        });
        return total;
    }, [selectedFoodIds, pickCounts, isFixed, getFoodWeight]);

    const priceRange = totalIndividualPrice.min !== totalIndividualPrice.max;

    const selectedByCategory = useMemo(() => {
        const map = new Map<string, { food_ids: number[]; category_id: number }>();
        menuItems.forEach((f) => {
            if (!selectedFoodIds.has(f.id)) return;
            if (!map.has(f.category_name)) map.set(f.category_name, { food_ids: [], category_id: f.category_id });
            map.get(f.category_name)!.food_ids.push(f.id);
        });
        return Array.from(map.entries())
            .filter(([, { food_ids }]) => food_ids.length > 0)
            .map(([name, { food_ids, category_id }]) => ({
                name,
                food_ids,
                pick: isFixed ? food_ids.length : (pickCounts[category_id] ?? 0),
            }));
    }, [selectedFoodIds, pickCounts, isFixed]);

    const isFoodDisabled = (foodId: number, weight: number) => {
        if (selectedFoodIds.has(foodId)) return false;
        if (totalWeight + (weight || 0) > 100) return true;
        if (isFixed) return false;
        if (selectedFoodIds.size >= 10) return true;
        return false;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            showToast('error', menuPage.create_menu_error_name);
            return;
        }
        if (priceRange) {
            const disc = Number(formData.discount || 0);
            if (disc < -50 || disc > 50) {
                showToast('error', menuPage.create_menu_error_price);
                return;
            }
        } else if (!formData.price || Number(formData.price) <= 0) {
            showToast('error', menuPage.create_menu_error_price);
            return;
        }
        if (selectedFoodIds.size === 0) {
            showToast('error', menuPage.create_menu_error_foods);
            return;
        }
        if (!isFixed) {
            for (const cat of categoryItems) {
                const selected = menuItems.filter(
                    (f) => f.category_id === cat.id && selectedFoodIds.has(f.id)
                ).length;
                const pick = pickCounts[cat.id] ?? 0;
                if (pick > selected) {
                    showToast('error', `Le nombre de plats à choisir (${pick}) dépasse le nombre de plats sélectionnés (${selected}) dans la catégorie "${cat.name}".`);
                    return;
                }
            }
        }

        setSubmitting(true);

        try {
            const payload: Record<string, unknown> = {
                name: formData.name,
                description: formData.description,
                groups: selectedByCategory,
            };
            if (priceRange) {
                payload.price = null;
                payload.discount_percentage = Number(formData.discount) || 0;
            } else {
                payload.price = Number(formData.price);
                payload.discount_percentage = null;
            }
            await api.post(API.MENUS, payload);
            showToast('success', menuPage.create_menu_success);
            setFormData({ name: '', description: '', price: '', discount: '0' });
            setSelectedFoodIds(new Set());
        } catch (err: unknown) {
            const apiError = err as {
                response?: {
                    data?: {
                        message?: string;
                        errors?: Record<string, string[]>;
                    }
                };
                userMessage?: string;
            };

            if (apiError.response?.data?.errors) {
                const firstError = Object.values(apiError.response.data.errors)[0][0];
                showToast('error', firstError);
            } else {
                showToast('error', apiError.userMessage || apiError.response?.data?.message || apiErrors.default_fallback);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const getFoodVariantInfo = useCallback((foodId: number) => {
        const variants = foodUpdates.get(foodId)?.variants ?? [];
        const menuVariants = variants.filter((v) => v.type === 'menu');
        if (menuVariants.length > 0) {
            return menuVariants;
        }
        return variants.filter((v) => v.type === 'food');
    }, [foodUpdates]);

    return (
        <div className="max-w-6xl mx-auto px-4 py-12">
            <motion.div {...fadeInUp}>
                <h1 className="text-4xl font-black mb-2 text-app-text dark:text-white">{menuPage.create_menu_title}</h1>
                <p className="text-app-muted mb-8">{menuPage.create_menu_subtitle}</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-bold dark:text-white">{menuPage.create_menu_select_foods}</h3>
                            <div className="text-sm text-app-muted">
                                {selectedFoodIds.size > 0 && (
                                        <span className="text-app-text font-semibold">
                                            {isFixed
                                                ? `${selectedFoodIds.size} selected · Total weight: ${totalWeight}%`
                                                : `${selectedFoodIds.size} / 10 selected · Total weight: ${totalWeight}%`}
                                        </span>
                                )}
                            </div>
                        </div>

                        {categoryItems.map((category) => {
                            const catFoods = menuItems.filter((f) => f.category_id === category.id);
                            if (catFoods.length === 0) return null;
                            const catPick = pickCounts[category.id] ?? 0;

                            return (
                                <div key={category.id} className="mb-6 last:mb-0">
                                    <h4 className="font-bold text-lg text-app-primary mb-3 flex items-center gap-2">
                                        <span>{category.ficon}</span>
                                        <span>{category.name}</span>
                                        {!isFixed && (
                                            <span className="ml-auto flex items-center gap-2 text-sm font-normal text-app-muted">
                                                <button
                                                    type="button"
                                                    onClick={() => handlePickCountChange(category.id, -1)}
                                                    className="w-7 h-7 rounded-full border border-app-border flex items-center justify-center hover:bg-app-bg disabled:opacity-30"
                                                    disabled={catPick <= 0}
                                                >
                                                    –
                                                </button>
                                                <span className="font-semibold text-app-text min-w-[1.5rem] text-center">
                                                    {catPick}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => handlePickCountChange(category.id, 1)}
                                                    className="w-7 h-7 rounded-full border border-app-border flex items-center justify-center hover:bg-app-bg disabled:opacity-30"
                                                    disabled={catPick >= catFoods.length}
                                                >
                                                    +
                                                </button>
                                            </span>
                                        )}
                                    </h4>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                        {catFoods.map((food) => {
                                            const isSelected = selectedFoodIds.has(food.id);
                                            const foodVariants = getFoodVariantInfo(food.id);
                                            const firstVariant = foodVariants[0];
                                            const hasMultiple = foodVariants.length > 1;
                                            const foodWeight = getFoodWeight(food);
                                            const foodUnit = getFoodUnit(food);
                                            const disabled = isFoodDisabled(food.id, foodWeight);

                                            return (
                                                <Button
                                                    variant="outline"
                                                    type="button"
                                                    key={food.id}
                                                    onClick={() => !disabled && toggleFood(food.id)}
                                                    disabled={disabled}
                                                    className={cn(
                                                        'relative flex flex-col rounded-xl overflow-hidden border-2 transition-all duration-200 text-left',
                                                        isSelected
                                                            ? 'border-app-primary ring-2 ring-app-primary/20 bg-app-primary/5'
                                                            : disabled
                                                              ? 'border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 opacity-50 cursor-not-allowed'
                                                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-750 hover:border-app-primary/50 hover:shadow-md cursor-pointer'
                                                    )}
                                                >
                                                    <div className="relative aspect-[4/3] bg-app-bg overflow-hidden">
                                                        <OptimizedImage
                                                            src={
                                                                food.item_image ||
                                                                specialtiesSection.food_image_fallback
                                                            }
                                                            alt={food.name}
                                                            className="w-full h-full object-cover"
                                                            onError={(
                                                                e: React.SyntheticEvent<HTMLImageElement, Event>
                                                            ) => {
                                                                (e.target as HTMLImageElement).src =
                                                                    specialtiesSection.food_image_fallback;
                                                            }}
                                                        />
                                                        {foodWeight > 0 && (
                                                            <div className="absolute bottom-2 left-2 z-10 bg-white/90 dark:bg-gray-800/90 text-app-primary text-[10px] font-black px-1.5 py-0.5 rounded-md border border-app-primary">
                                                                {foodWeight}%
                                                            </div>
                                                        )}
                                                        {isSelected && (
                                                            <div className="absolute top-2 right-2 z-10 bg-app-primary text-white text-xs font-black w-6 h-6 rounded-full flex items-center justify-center shadow-md">
                                                                ✓
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col p-2 gap-1 flex-1">
                                                        <span className="font-bold text-xs dark:text-white leading-tight line-clamp-2">
                                                            {food.name}
                                                        </span>
                                                        {firstVariant && (
                                                            <span className="text-[10px] text-app-muted leading-tight">
                                                                {hasMultiple ? (
                                                                    <>
                                                                        {foodVariants
                                                                            .map(
                                                                                (v) =>
                                                                                    `${v.name} ${v.portion}${foodUnit}`
                                                                            )
                                                                            .join(' · ')}
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {firstVariant.name} {firstVariant.portion}
                                                                        {foodUnit} ·{' '}
                                                                        {formatPrice(Number(firstVariant.price))}
                                                                    </>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </Button>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}

                        {selectedFoodIds.size === 0 && (
                            <p className="text-sm text-app-muted text-center py-8">
                                {menuPage.create_menu_select_hint}
                            </p>
                        )}

                        {selectedFoodIds.size > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex flex-wrap gap-x-6 gap-y-1 justify-center text-sm text-app-muted">
                                    <span>
                                        Selected:{' '}
                                        <strong className="text-app-text">
                                            {isFixed
                                                ? `${selectedFoodIds.size} items`
                                                : `${selectedFoodIds.size} / 10 items`}
                                        </strong>
                                    </span>
                                    <span>
                                        Total weight: <strong className="text-app-text">{totalWeight}%</strong>
                                    </span>
                                    <span>
                                        Individual total:{' '}
                                        <strong className="text-app-text">
                                            {totalIndividualPrice.min === totalIndividualPrice.max
                                                ? formatPrice(totalIndividualPrice.min)
                                                : `${formatPrice(totalIndividualPrice.min)} – ${formatPrice(totalIndividualPrice.max)}`}
                                        </strong>
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl shadow-xl space-y-4">
                        <h3 className="text-xl font-bold dark:text-white">{menuPage.create_menu_details_heading}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="menu-name" className={labelClass}>{menuPage.create_menu_name_label}</label>
                                <input
                                    required
                                    id="menu-name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={inputClass}
                                    placeholder={menuPage.create_menu_name_placeholder}
                                />
                            </div>
                            <div>
                                {priceRange ? (
                                    <>
                                        <label htmlFor="menu-discount" className={labelClass}>Réduction (%)</label>
                                        <input
                                            type="number"
                                            step="0.10"
                                            min="-50"
                                            max="50"
                                            id="menu-discount"
                                            name="discount"
                                            value={formData.discount}
                                            onChange={handleChange}
                                            className={inputClass}
                                            placeholder="Pourcentage de réduction"
                                        />
                                        {(() => {
                                            const pct = Number(formData.discount || 0) / 100;
                                            const discMin = Math.max(0, totalIndividualPrice.min * (1 - pct));
                                            const discMax = Math.max(0, totalIndividualPrice.max * (1 - pct));
                                            return (
                                                <p className="text-xs text-app-muted mt-1">
                                                    Prix de ce menu :&nbsp;
                                                    <s>{formatPrice(totalIndividualPrice.min)}</s>&nbsp;
                                                    {formatPrice(discMin)}&nbsp;
                                                    –&nbsp;
                                                    <s>{formatPrice(totalIndividualPrice.max)}</s>&nbsp;
                                                    {formatPrice(discMax)}
                                                </p>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <>
                                        <label htmlFor="menu-price" className={labelClass}>{menuPage.create_menu_price_label}</label>
                                        <input
                                            required
                                            type="number"
                                            step="0.01"
                                            id="menu-price"
                                            name="price"
                                            value={formData.price}
                                            onChange={handleChange}
                                            className={inputClass}
                                            placeholder={menuPage.create_menu_price_placeholder}
                                        />
                                        <p className="text-xs text-app-muted mt-1">
                                            {menuPage.create_menu_total_value}{' '}
                                            <strong>{formatPrice(totalIndividualPrice.min)}</strong>
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                        <div>
                            <label htmlFor="menu-description" className={labelClass}>{menuPage.create_menu_description_label}</label>
                            <textarea
                                id="menu-description"
                                name="description"
                                value={formData.description}
                                onChange={handleChange}
                                className={inputClass}
                                rows={2}
                                placeholder={menuPage.create_menu_description_placeholder}
                            />
                        </div>
                    </div>

                    <Button type="submit" disabled={submitting} size="lg" className="w-full">
                        {submitting ? menuPage.create_menu_submitting : menuPage.create_menu_submit}
                    </Button>
                </form>
            </motion.div>
        </div>
    );
}
